import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { updatesHubService } from '@/services/updates-hub.service';
import { BadgesState, NumericBadgeKey } from '@/types/updates-hub';

interface UpdatesHubState {
  badges: BadgesState;
  loading: boolean;
  error: string | null;
}

const initialState: UpdatesHubState = {
  badges: {
    news: 0,
    newsExpert: 0,
    expertPosts: 0,
    notifications: 0,
    chat: 0,
    payments: 0,
    calendar: 0,
    calendarByDates: [],
    questionnaires: 0,
    experts: 0,
    appointments: 0,
  },
  loading: false,
  error: null,
};

export const fetchBadges = createAsyncThunk(
  'updatesHub/fetchBadges',
  async (_, { rejectWithValue }) => {
    try {
      const badges = await updatesHubService.getBadges();
      return badges;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch badges');
    }
  }
);

const updatesHubSlice = createSlice({
  name: 'updatesHub',
  initialState,
  reducers: {
    setBadges: (state, action: PayloadAction<BadgesState>) => {
      state.badges = action.payload;
    },
    updateBadge: (state, action: PayloadAction<{ key: NumericBadgeKey; value: number }>) => {
      const { key, value } = action.payload;
      (state.badges[key] as number) = value;
    },
    incrementBadge: (state, action: PayloadAction<NumericBadgeKey>) => {
      const key = action.payload;
      (state.badges[key] as number)++;
    },
    decrementBadge: (state, action: PayloadAction<NumericBadgeKey>) => {
      const key = action.payload;
      if ((state.badges[key] as number) > 0) {
        (state.badges[key] as number)--;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBadges.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBadges.fulfilled, (state, action: PayloadAction<BadgesState>) => {
        state.loading = false;
        state.badges = action.payload;
      })
      .addCase(fetchBadges.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setBadges, updateBadge, incrementBadge, decrementBadge } = updatesHubSlice.actions;
export default updatesHubSlice.reducer;
